import { memo } from 'react';

import { Card, CardActions, CardContent, Grid, Typography, SxProps, Box, useTheme, Collapse } from '@mui/material';

import { GoogleMapTemplate, Button, Maybe, BffWorkingHours, Tabs, NamedAttribute, Facility } from '@/types/generated';
import { getWorkingHoursFromBff, groupDays, resolvePhoneNumberFormat } from '@/utils';
import { metaData, sys } from '@/constants';
import { useAppContext, useFacilityContext } from '@/context';
import TAGSvgIcon from '@/components/TAGSvgIcon';
import ContentfulButton from '@/components/ContentfulButton';
import { SchedulingSystemType } from '@/types';

import TAGLink from '../../../TAGLink';
import TAGButton from '../../../TAGButton';
import { getOfficeDetailsUrl, getScheduleButtonUrl } from '../../GoogleMap.helpers';
import { getMarkerSimpleIcon, getModernIconType } from '../ModernGoogleMap.helpers';
import SubscriptionForm from '../../../SubscriptionForm';

import {
	getAddressLinkStyles,
	cardActionStyles,
	iconButtonStyles,
	getLocationCardStyles,
	locationNameAndPhoneWrapperStyles,
	phoneLinkStyles,
	pinMarketStyles,
} from './LocationCard.styles';
import LocationCardServices from './LocationCardServices';

export interface INearbyFacility extends Facility {
	attributes?: Maybe<NamedAttribute>[];
}
export interface ILocationCardProps {
	id?: string | null;
	location: INearbyFacility;
	isSmallScreen?: boolean;
	distance?: number;
	index?: number;
	locationScheduleButtonUrl?: GoogleMapTemplate['locationScheduleButtonUrl'];
	googleMapsBusinessQuery?: GoogleMapTemplate['googleMapsBusinessQuery'];
	defaultImage?: GoogleMapTemplate['defaultImage'];
	selectedImage?: GoogleMapTemplate['selectedImage'];
	locationRating?: boolean;
	locationScheduling?: { schedulingSystem?: SchedulingSystemType; accountId?: string };
	facilityBasePath?: string;
	isCurrentSelectedFacility?: boolean;
	holidayHoursMessageText?: string;
	revspringAccountId?: string | null;
	facilityBadgeLabel?: string | null;
	facilityButtonLabel?: string | null;
	scheduleButton?: Maybe<Button>;
	officeDetailsButton?: Maybe<Button>;
	setSelectedFacilityId?: (facilityId: string | null) => void;
	availableServicesTabs?: Maybe<Tabs>;
}

export default memo(function LocationCard({
	id,
	index,
	location,
	isCurrentSelectedFacility,
	distance,
	googleMapsBusinessQuery,
	scheduleButton,
	officeDetailsButton,
	facilityBasePath,
	locationScheduling,
	setSelectedFacilityId,
	isSmallScreen,
	availableServicesTabs,
}: Readonly<ILocationCardProps>) {
	const { config } = useAppContext();
	const { facilityBrand } = useFacilityContext();
	const schedulingPath = config.features.otherApps.schedulingPath;
	const { stateCode: state, city, address1: address, zipCode } = location.address;
	const locationPhoneNumber = location?.technicalInfrastructure?.phoneNumber ?? location?.phoneNumber ?? '';
	const locationId = location.code.split('_')[1];
	const theme = useTheme();
	const { comingSoon, attributes } = location;
	const iconText = typeof index === 'number' ? `${index + 1}` : '';
	const iconType = getModernIconType(isCurrentSelectedFacility, comingSoon);
	const updatedDistance = distance ? distance.toFixed(1) : 0;
	const workingHours = getWorkingHoursFromBff((location.workingHours ?? []) as BffWorkingHours[], true);
	const onChangeLocation = () => {
		if (setSelectedFacilityId) {
			if (isCurrentSelectedFacility) {
				setSelectedFacilityId(null);
			} else {
				setSelectedFacilityId(location.code);
			}
		}
	};
	const scheduleButtonComponent = scheduleButton && (
		<Grid item xs={isCurrentSelectedFacility ? 5.875 : 12}>
			<ContentfulButton
				key={scheduleButton.sys.id}
				{...scheduleButton}
				dataTestId="location_card_schedule_button"
				buttonLink={getScheduleButtonUrl({
					schedulingPathFromConfig: schedulingPath,
					facilityBrand,
					location,
					locationScheduling,
				})}
				fullWidth
				variant={isCurrentSelectedFacility ? scheduleButton.variant : 'Secondary Default'}
				size={isCurrentSelectedFacility ? scheduleButton.size : 'extraSmall'}
				defaultVariant="secondaryDefault"
				analyticsContext={{
					...scheduleButton?.analyticsContext,
					appointmentSelectedFacilityId: location?.code,
					appointmentSelectedFacilityName: location?.name,
					contentfulMetadata: metaData,
					sys: sys,
					_id: '',
				}}
			/>
		</Grid>
	);
	const officeDetailsButtonComponent = isCurrentSelectedFacility && officeDetailsButton && (
		<Grid item xs={5.875}>
			<ContentfulButton
				key={officeDetailsButton.sys.id}
				{...officeDetailsButton}
				fullWidth
				dataTestId="location_card_office_details_button"
				buttonLink={getOfficeDetailsUrl({
					facilityBasePath,
					themeId: config.themeId,
					address: location.address,
				})}
				defaultVariant="primaryHC"
			/>
		</Grid>
	);

	const hasAvailableServices = !!availableServicesTabs;

	return (
		<Card
			id={id ?? ''}
			data-test-id={`location_card_${location.code}`}
			sx={getLocationCardStyles({ isCurrentSelectedFacility, isSmallScreen }) as SxProps}
		>
			<CardContent sx={{ padding: '0 0 1.5rem' }}>
				<Grid item container xs={12}>
					{/* Distance */}
					<Grid item xs={2} sx={pinMarketStyles} data-test-id="location_card_distance">
						{getMarkerSimpleIcon(theme, iconType, iconText)}
						<Typography variant="captionMedium">{updatedDistance} mi</Typography>
					</Grid>
					{/* Location name and phone number */}
					<Grid item xs={9}>
						<Box sx={locationNameAndPhoneWrapperStyles}>
							<Typography data-test-id="location_card_title" variant="bodyMediumSemiBold">
								{location.name}
							</Typography>
							{comingSoon ? (
								<Box display="flex" flexDirection="column" gap={0.25} alignItems="end">
									<Typography
										color="accent02.dark"
										variant="bodySmallSemiBold"
										display="flex"
										alignItems="center"
										gap={0.25}
										data-test-id="location_card_coming_soon_label"
									>
										<TAGSvgIcon icon="AddLocationAltOutlined" size={16} />
										Coming Soon!
									</Typography>
									<Typography variant="captionMedium" data-test-id="location_card_open_date">
										Opening {location.openDate}
									</Typography>
								</Box>
							) : (
								<TAGButton
									size="S"
									startIcon={<TAGSvgIcon icon="CallOutlined" size={24} />}
									variant="primaryHC"
									href={`tel:${resolvePhoneNumberFormat(locationPhoneNumber) as string}`}
									sx={phoneLinkStyles}
									dataTestId="location_card_phone_number"
								>
									{resolvePhoneNumberFormat(locationPhoneNumber)}
								</TAGButton>
							)}
						</Box>
						{/* Address */}
						<TAGLink
							dataTestId="location_card_address"
							href={googleMapsBusinessQuery?.replace(
								/\[address\]/gi,
								`${address as string}, ${city as string}, ${state as string} ${zipCode as string}`
							)}
							openInNewTab
							schedulingDataLayer={{
								event: 'saa_flow',
								step_name: 'find_a_location',
								brand: 'WellNow',
								clinic_city: city,
								clinic_location: address,
								clinic_state: state,
							}}
							linkSx={getAddressLinkStyles({ isSmallScreen })}
						>
							<Typography
								data-test-id="section_directions_text"
								variant="bodySmallBook"
								color="neutrals.tertiary"
							>
								{address}
							</Typography>
							<Typography
								data-test-id="section_directions_text"
								variant="bodySmallBook"
								color="neutrals.tertiary"
							>
								{city}, {state} {zipCode}
							</Typography>
						</TAGLink>
					</Grid>
					{/* Extend button */}
					<Grid item xs={1} sx={{ textAlign: 'center' }}>
						{!comingSoon && (
							<TAGButton
								dataTestId="location_card_extend_button"
								variant="tertiaryDefault"
								size="S"
								onClick={onChangeLocation}
								aria-label={
									isCurrentSelectedFacility ? `expand location card` : `collapse location card`
								}
								sx={iconButtonStyles}
							>
								<TAGSvgIcon icon={isCurrentSelectedFacility ? 'SortUp' : 'SortDown'} size={24} />
							</TAGButton>
						)}
					</Grid>
				</Grid>
				{(!!workingHours.length || hasAvailableServices) && (
					<Collapse in={isCurrentSelectedFacility}>
						{!!workingHours.length && (
							<Box py="2rem" data-test-id="location_card_working_hours">
								<Typography color="primary.main" variant="bodyMediumBold">
									Hours of Operation
								</Typography>
								<Grid container gap={1} sx={{ flexDirection: 'column' }}>
									{groupDays(workingHours).map((item, index) => (
										<Grid container key={index} gap={1.5} alignItems="center">
											<Typography color="neutrals.tertiary" variant="bodySmallBook" width="40%">
												{item.day}
											</Typography>
											<Typography color="neutrals.tertiary" variant="bodySmallSemiBold">
												{item.hours}
											</Typography>
										</Grid>
									))}
								</Grid>
							</Box>
						)}
						{availableServicesTabs && (
							<LocationCardServices
								data-test-id="location_card_available_services"
								key="available_services"
								availableServicesTabs={availableServicesTabs}
								locationAttributes={attributes as NamedAttribute[]}
							/>
						)}
					</Collapse>
				)}
			</CardContent>
			<CardActions data-test-id="location_card_actions" sx={cardActionStyles}>
				{comingSoon ? (
					<SubscriptionForm
						description="Get up-to-date news on our opening!"
						descriptionPosition="bottom"
						additionalSubscriptionData={{ subscribed_location_id: locationId }}
					/>
				) : (
					<Grid container item xs={12} gap={1}>
						{officeDetailsButtonComponent}
						{scheduleButtonComponent}
					</Grid>
				)}
			</CardActions>
		</Card>
	);
});
